'use client';

import { c as _c } from "react/compiler-runtime";
import { useRouter as usePagesRouter } from 'next/compat/router';
import { useParams, usePathname, useRouter as useAppRouter, useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import { getPageRoutePattern } from 'shared/utils/get-page-route-pattern';
import type { ParsedUrlQuery } from 'querystring';
const getSearchParamsQuery = (searchParamsString?: string): ParsedUrlQuery => {
  const query: ParsedUrlQuery = {};
  if (!searchParamsString) return query;
  const searchParams = new URLSearchParams(searchParamsString);
  searchParams.forEach((value, key) => {
    const currentValue = query[key];
    if (currentValue === undefined) {
      query[key] = value;
      return;
    }
    query[key] = Array.isArray(currentValue) ? [...currentValue, value] : [currentValue, value];
  });
  return query;
};
const getParamsQuery = (params: Record<string, string | string[]> | null): ParsedUrlQuery => {
  if (!params) return {};
  return Object.entries(params).reduce<ParsedUrlQuery>((query, [key, value]) => {
    query[key] = value;
    return query;
  }, {});
};
const getPathnameFromAsPath = (asPath: string) => {
  const [pathWithoutHash = '', hash] = asPath.split('#');
  const [pathname = ''] = pathWithoutHash.split('?');
  return {
    hash,
    pathname
  };
};
const buildPathFromQuery = (asPath: string, query: ParsedUrlQuery) => {
  const {
    hash,
    pathname
  } = getPathnameFromAsPath(asPath);
  const searchParams = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined) return;
    if (Array.isArray(value)) {
      value.forEach(item => searchParams.append(key, item));
      return;
    }
    searchParams.set(key, value);
  });
  const queryString = searchParams.toString();
  const pathWithQuery = queryString ? `${pathname}?${queryString}` : pathname;
  return hash ? `${pathWithQuery}#${hash}` : pathWithQuery;
};
interface RouterOptions {
  scroll?: boolean;
  shallow?: boolean;
}
interface RouteNavigation {
  /**
   * Pages-compatible route template.
   * Example: `/courses/[courseId]` instead of `/courses/123`.
   */
  pathname: string;
  asPath: string;
  query: ParsedUrlQuery;
  push: (route: string, options?: RouterOptions) => Promise<boolean>;
  replace: (route: string, options?: RouterOptions) => Promise<boolean>;
  replaceQuery: (query: ParsedUrlQuery, options?: RouterOptions) => Promise<boolean>;
}
export const useRouter = () => {
  const $ = _c(27);
  const pagesRouter = usePagesRouter();
  const appRouter = useAppRouter();
  const appPathname = usePathname();
  const appParams = useParams();
  const appSearchParams = useSearchParams();
  let t0;
  if ($[0] !== appSearchParams) {
    t0 = appSearchParams?.toString();
    $[0] = appSearchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const appSearchParamsString = t0;
  let t1;
  if ($[2] !== appPathname) {
    t1 = appPathname ? getPageRoutePattern(appPathname) : undefined;
    $[2] = appPathname;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const appRoutePattern = t1;
  const pagesPathname = pagesRouter?.pathname;
  const pagesAsPath = pagesRouter?.asPath;
  const pagesQuery = pagesRouter?.query;
  let t2;
  bb0: {
    if (!appPathname) {
      t2 = "";
      break bb0;
    }
    t2 = appSearchParamsString ? `${appPathname}?${appSearchParamsString}` : appPathname;
  }
  const appAsPath = t2;
  let t3;
  if ($[4] !== appParams) {
    t3 = getParamsQuery(appParams);
    $[4] = appParams;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== appSearchParamsString) {
    t4 = getSearchParamsQuery(appSearchParamsString);
    $[6] = appSearchParamsString;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = {
      ...t3,
      ...t4
    };
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const appQuery = t5;
  const t6 = pagesPathname ?? appRoutePattern ?? "";
  const t7 = pagesAsPath ?? appAsPath;
  const t8 = pagesQuery ?? appQuery;
  let t10;
  let t9;
  if ($[11] !== appRouter || $[12] !== pagesRouter) {
    t9 = async (route, options) => {
      if (pagesRouter) {
        return pagesRouter.push(route, undefined, options);
      }
      appRouter.push(route, {
        scroll: options?.scroll
      });
      return true;
    };
    t10 = async (route_0, options_0) => {
      if (pagesRouter) {
        return pagesRouter.replace(route_0, undefined, options_0);
      }
      appRouter.replace(route_0, {
        scroll: options_0?.scroll
      });
      return true;
    };
    $[11] = appRouter;
    $[12] = pagesRouter;
    $[13] = t10;
    $[14] = t9;
  } else {
    t10 = $[13];
    t9 = $[14];
  }
  let t11;
  if ($[15] !== appAsPath || $[16] !== appRouter || $[17] !== pagesAsPath || $[18] !== pagesRouter) {
    t11 = async (query, options_1) => {
      const route_1 = buildPathFromQuery(pagesAsPath ?? appAsPath, query);
      if (pagesRouter) {
        return pagesRouter.replace(route_1, undefined, options_1);
      }
      appRouter.replace(route_1, {
        scroll: options_1?.scroll
      });
      return true;
    };
    $[15] = appAsPath;
    $[16] = appRouter;
    $[17] = pagesAsPath;
    $[18] = pagesRouter;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t10 || $[21] !== t11 || $[22] !== t6 || $[23] !== t7 || $[24] !== t8 || $[25] !== t9) {
    t12 = {
      pathname: t6,
      asPath: t7,
      query: t8,
      push: t9,
      replace: t10,
      replaceQuery: t11
    };
    $[20] = t10;
    $[21] = t11;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
};